import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Header, Icon, Input, Label, Grid, Dropdown, ListItem, List, ListContent, Button, Message } from 'semantic-ui-react'
import { ActionButton, LoadingWrapper } from "../common";
import { fieldMap } from '../common/helpers';
import { canEditAll, getCSRFToken, fetchWithAuth } from '../auth';

import "../../css/common.css";


export const DeviceSubTypeDetails = () => {
    const [deviceSubType, setDeviceSubType] = useState({ 'frontendFields': [] });
    const [deviceTypes, setDeviceTypes] = useState([]);
    const [selectedDeviceType, setSelectedDeviceType] = useState('');
    const [initialDeviceSubType, setInitialDeviceSubType] = useState([]);
    const [availableFields, setAvailableFields] = useState([])
    const [selectedFields, setSelectedFields] = useState([])
    const [succeeded, setSucceeded] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [loaded, setLoaded] = useState(false)
    const [typesLoaded, setTypesLoaded] = useState(false)
    const [fieldsLoaded, setFieldsLoaded] = useState(false)
    const { id } = useParams();
    const [isLandscape, setIsLandscape] = useState(window.matchMedia('(orientation:landscape)').matches)

    const navigate = useNavigate()

    const updateLandscape = () => {
        setIsLandscape(window.matchMedia('(orientation:landscape)').matches)
    }
    window.addEventListener('resize', updateLandscape)

    const normalizeType = (type) => {
        type.frontendFields = type.frontendFields.split(",")
        type.frontendFields.sort()
    }

    const denormalizeType = (type) => {
        type.frontendFields = type.frontendFields.join(",")
    }

    useEffect(() => {
        fetchDeviceSubtypeDetails();
        fetchDeviceTypes();
        fetchAvailableFields();
    }, [id]);

    const fetchDeviceSubtypeDetails = async () => {
        setLoaded(false)
        const response = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + `devicesubtypes/${id}/`, {
            headers: { 'X-CSRFToken': getCSRFToken() }
        });
        const data = await response.json();
        normalizeType(data)
        setDeviceSubType(data)
        setSelectedDeviceType(data.assetDeviceType.id)
        setInitialDeviceSubType(data)
        setLoaded(true)
    };

    const fetchDeviceTypes = async () => {
        setTypesLoaded(false)
        const response = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + 'devicetypes/', {
            headers: { 'X-CSRFToken': getCSRFToken() },
        });
        const data = await response.json();
        setDeviceTypes(data);
        setTypesLoaded(true)
    };

    const fetchAvailableFields = async () => {
        setFieldsLoaded(false)
        const response = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + 'availablefields/', {
            headers: { 'X-CSRFToken': getCSRFToken() }
        });
        const data = await response.json();
        setAvailableFields(data);
        setFieldsLoaded(true)
    }

    const handleDeviceSubTypeUpdate = async () => {
        setLoaded(false)

        const denormalizedType = structuredClone(deviceSubType)
        denormalizedType.frontendFields = [...denormalizedType.frontendFields, ...selectedFields]
        denormalizeType(denormalizedType)
        const response = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + `/devicesubtypes/${id}/`, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': getCSRFToken()
            },
            body: JSON.stringify({
                name: denormalizedType.name,
                deviceType: selectedDeviceType,
                frontendFields: denormalizedType.frontendFields
            }),
        });

        window.scrollTo({ top: 0, behavior: 'smooth' })

        if (response.ok) {
            setErrorMessage('')
            setSucceeded(true)
            const updatedDeviceSubType = await response.json();
            normalizeType(updatedDeviceSubType)
            setDeviceSubType(updatedDeviceSubType);
            setInitialDeviceSubType(updatedDeviceSubType);
            setSelectedDeviceType(updatedDeviceSubType.assetDeviceType.id)
            setSelectedFields([])
        } else {
            const respJson = await response.json()
            const errors = []
            for (const field in respJson) {
                const error = field + ": " + respJson[field]
                errors.push(error)
            }
            setSucceeded(false)
            setErrorMessage('Could not save.  ' + errors.join(", "))
        }
        setLoaded(true)
    };

    const handleDeviceSubTypeNameChange = (e) => {
        setDeviceSubType({ ...deviceSubType, name: e.target.value });
    };

    const handleCancel = () => {
        setDeviceSubType({ ...initialDeviceSubType });
    };

    const updateSelectedDeviceType = (event, data) => {
        setSelectedDeviceType(data.value)
    }

    const updateSelectedFields = (event, data) => {
        setSelectedFields(data.value)
    }

    const handleRemoveField = (field) => {
        const newDeviceSubType = structuredClone(deviceSubType)
        newDeviceSubType.frontendFields = newDeviceSubType.frontendFields.filter(f => f !== field)
        setDeviceSubType(newDeviceSubType)
    };

    async function createChecklist() {
        const response = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + `/devicesubtypes/${id}/checklist/`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': getCSRFToken()
            },
            body: JSON.stringify({
                name: 'Checklist',
                content: ''
            })
        });
        if(!response.ok) {
            setErrorMessage("Something went wrong.")
            return
        }
        navigate('checklist')
    }


    return (
        <LoadingWrapper text="Loading asset subtype" loading={!loaded}>
            <div align="center">
                {succeeded && <Message style={{ width: "75%" }} positive>Saved successfully!</Message>}
                {errorMessage !== '' && <Message style={{ width: "75%" }} negative>{errorMessage}</Message>}
                <div>
                    <div>
                        <Header size='medium'>
                            Asset SubType: {deviceSubType.name}</Header>
                    </div>
                    <div>
                        <Grid centered>
                            <Grid.Row><Grid.Column width={4} textAlign='center'></Grid.Column></Grid.Row>
                            <Grid.Row> <Grid.Column width={4} textAlign='right'><Label size="large" active pointing="right">
                                Asset SubType Name</Label></Grid.Column>
                                <Grid.Column width={8}> <Input
                                    type="text"
                                    id="deviceSubTypeName"
                                    value={deviceSubType.name}
                                    onChange={handleDeviceSubTypeNameChange}
                                    readOnly={!canEditAll()}
                                />
                                </Grid.Column>
                            </Grid.Row>
                            <Grid.Row>
                                <Grid.Column width={4} textAlign='right'><Label size="large" active pointing="right">
                                    Device Type:</Label></Grid.Column>
                                <Grid.Column width={8}>
                                    <Dropdown
                                        style={{ width: isLandscape ? "50%" : "100%" }}
                                        placeholder="Name" value={selectedDeviceType}
                                        onChange={canEditAll() ? updateSelectedDeviceType : () => { }}
                                        fluid search selection options={
                                            deviceTypes.map(devType => {
                                                return {
                                                    key: devType.id,
                                                    value: devType.id,
                                                    text: devType.name
                                                }
                                            })
                                        }
                                        loading={!typesLoaded} />


                                </Grid.Column></Grid.Row>

                            <Grid.Row> <Grid.Column width={4} textAlign='right'><Label size="large" active pointing="right">
                                Fields </Label></Grid.Column>
                                <Grid.Column width={8}>{!(deviceSubType.frontendFields) || deviceSubType.frontendFields.length === 0 ? (
                                    <p>No fields.</p>
                                ) : (
                                    <List relaxed className="list">
                                        {deviceSubType.frontendFields.map((field, i) => {
                                            return (
                                                <div key={i} className='list-item'>
                                                    <ListItem key={field}>
                                                        <ListContent style={{ fontWeight: "bold" }} as='a'>{fieldMap[field]}</ListContent>
                                                    </ListItem>
                                                    {canEditAll() &&
                                                        <Button
                                                            onClick={() => handleRemoveField(field)}
                                                            style={{ width: "fit-content", height: "fit-content", padding: "0.5rem", margin: "0" }}>
                                                            <Icon style={{ margin: "0" }} color='red' link name='close' />
                                                        </Button>}
                                                </div>)
                                        })}
                                    </List>
                                )
                                }

                                </Grid.Column>
                            </Grid.Row>

                            {canEditAll() && <Grid.Row>
                                <Grid.Column width={4} textAlign='right'><Label size="large" active pointing="right">
                                    Add Fields:</Label></Grid.Column>
                                <Grid.Column width={8}>
                                    <Dropdown
                                        style={{ width: isLandscape ? "50%" : "100%" }}
                                        placeholder="Fields"
                                        value={selectedFields}
                                        onChange={updateSelectedFields}
                                        fluid multiple search selection options={
                                            availableFields.filter(field => !deviceSubType.frontendFields.some(f => f === field))
                                                .map(field => {
                                                    return {
                                                        key: field,
                                                        value: field,
                                                        text: fieldMap[field]
                                                    }
                                                })
                                        }
                                        loading={!fieldsLoaded} />
                                </Grid.Column>
                            </Grid.Row>}

                            {canEditAll() && <Grid.Row> <Grid.Column width={4} textAlign='right'><Label size="large" active pointing="right">
                                Checklist</Label></Grid.Column>
                                <Grid.Column style={{display: "flex", alignItems: "center"}} width={8}>
                                    {deviceSubType.checklist ?
                                        <Link style={{fontSize: "15px", fontWeight: "bold"}} to={'checklist'}>Checklist</Link>
                                        : <Button onClick={createChecklist}>Create checklist</Button>}
                                </Grid.Column>
                            </Grid.Row>}

                            {canEditAll() && <Grid.Row centered>
                                <ActionButton submitAction={handleDeviceSubTypeUpdate}
                                    resetHide={false}
                                    resetText={'Cancel'}
                                    showConfirmation={true}
                                    resetAction={handleCancel}
                                />
                            </Grid.Row>}

                        </Grid>
                    </div>
                </div>
            </div>
        </LoadingWrapper>
    )

}
